import { useEffect } from "react";
import styled from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";

import appleIcon from "./account-exists-assets/apple.svg";
import googlePlayWordmark from "./account-exists-assets/google-play-wordmark.svg";

const APP_STORE_URL = "https://apps.apple.com/us/app/built-with-science/id6446000532";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.bws.appv2";

type AccountExistsScreenProps = {
  onBack: () => void;
  source: "email_step" | "signup_gate";
};

const Screen = styled.main`
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  background: linear-gradient(180deg, #e7f0ff 10.6%, #ffffff 86.1%);
  color: #000000;
  display: flex;
  flex-direction: column;
  font-family: Lato, sans-serif;
`;

const ValidationBar = styled.div`
  align-items: center;
  background: #ffffff;
  box-sizing: border-box;
  color: #7a7a7a;
  display: flex;
  font-size: 12px;
  gap: 6px;
  justify-content: center;
  line-height: 1.5;
  min-height: 32px;
  padding: 7px 16px;
  text-align: center;
  width: 100%;
`;

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 0 auto;
  max-width: 375px;
  padding: 16px 24px 26px;
  width: 100%;
`;

const BackButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: 0;
  color: #7a7a7a;
  cursor: pointer;
  display: inline-flex;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 510;
  gap: 4px;
  line-height: 1.2;
  margin: -8px 0 0 -8px;
  padding: 8px 8px 8px 0;

  &:hover,
  &:focus-visible {
    color: #4a4a4a;
  }
`;

const BackIcon = styled.span`
  display: inline-flex;
  height: 24px;
  width: 24px;

  svg {
    display: block;
  }
`;

const Body = styled.section`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: 106px;
  text-align: center;

  @media screen and (max-height: 700px) {
    padding-top: 72px;
  }
`;

const EyeGlyph = styled.div`
  font-size: 64px;
  line-height: 1.15;
  margin-bottom: 12px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Title = styled.h1`
  color: #000000;
  font-family: Lato, sans-serif;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  margin: 0;
`;

const Copy = styled.p`
  color: #4a4a4a;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
  max-width: 311px;
`;

const StoreLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 64px;
  width: min(100%, 290px);

  @media screen and (max-height: 700px) {
    margin-top: 44px;
  }
`;

const StoreBadge = styled.a`
  align-items: center;
  background: #000000;
  border: 1px solid #a6a6a6;
  border-radius: 6px;
  box-sizing: border-box;
  color: #ffffff;
  display: flex;
  height: 40px;
  justify-content: center;
  overflow: hidden;
  text-decoration: none;
  width: 100%;
`;

const AppleBadgeContent = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const AppleIcon = styled.img`
  display: block;
  height: 24px;
  width: 20px;
`;

const AppleText = styled.span`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  width: 78px;
`;

const AppleKicker = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "SF Compact Text", Lato, sans-serif;
  font-size: 9px;
  font-weight: 500;
  line-height: 1;
`;

const AppleLabel = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "SF Compact Display", Lato, sans-serif;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.47px;
  line-height: 1;
`;

const GoogleBadgeContent = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const PlayTriangle = styled.span`
  display: block;
  height: 24px;
  position: relative;
  width: 21px;

  &::before {
    background: linear-gradient(135deg, #33d375 0 42%, #ffd440 42% 58%, #ff4b55 58% 100%);
    clip-path: polygon(0 0, 100% 50%, 0 100%);
    content: "";
    inset: 0;
    position: absolute;
  }

  &::after {
    background: #3f8cff;
    clip-path: polygon(0 0, 48% 50%, 0 100%);
    content: "";
    inset: 0;
    position: absolute;
  }
`;

const GoogleText = styled.span`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const GoogleKicker = styled.span`
  font-family: Lato, sans-serif;
  font-size: 10px;
  line-height: 1;
  text-transform: uppercase;
`;

const GoogleWordmark = styled.img`
  display: block;
  height: 15px;
  transform: scaleY(-1);
  width: 74px;
`;

const Helper = styled.p`
  color: #7a7a7a;
  font-size: 12px;
  font-style: italic;
  line-height: 1.5;
  margin: 48px 0 0;

  @media screen and (max-height: 700px) {
    margin-top: 36px;
  }
`;

export function AccountExistsScreen({ onBack, source }: AccountExistsScreenProps) {
  useEffect(() => {
    amplitude.track("account_exists_screen_viewed", { source });
  }, [source]);

  return (
    <Screen>
      <ValidationBar>
        <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M10.83 3.25L5.24 8.84L2.67 6.27"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
        <span>Existing account found</span>
      </ValidationBar>
      <Content>
        <BackButton onClick={onBack} type="button">
          <BackIcon aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </BackIcon>
          <span>Back</span>
        </BackButton>

        <Body>
          <EyeGlyph aria-hidden="true">👀</EyeGlyph>
          <Header>
            <Title>You’ve been here before!</Title>
            <Copy>
              You already have an account with us. Sign in to the Built With
              Science app to continue.
            </Copy>
          </Header>

          <StoreLinks aria-label="Download the Built With Science app">
            <StoreBadge href={APP_STORE_URL} rel="noreferrer" target="_blank">
              <AppleBadgeContent>
                <AppleIcon alt="" src={appleIcon} />
                <AppleText>
                  <AppleKicker>Download on the</AppleKicker>
                  <AppleLabel>App Store</AppleLabel>
                </AppleText>
              </AppleBadgeContent>
            </StoreBadge>
            <StoreBadge href={GOOGLE_PLAY_URL} rel="noreferrer" target="_blank">
              <GoogleBadgeContent>
                <PlayTriangle aria-hidden="true" />
                <GoogleText>
                  <GoogleKicker>Get it on</GoogleKicker>
                  <GoogleWordmark alt="Google Play" src={googlePlayWordmark} />
                </GoogleText>
              </GoogleBadgeContent>
            </StoreBadge>
          </StoreLinks>

          <Helper>Forgot your password? You can reset it in the app.</Helper>
        </Body>
      </Content>
    </Screen>
  );
}
